import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAccount, useConnect, useNetwork, useSwitchNetwork, useSignMessage } from "wagmi";
import { useAuth } from "../../context/AuthContext";
import AuthModal from "../../components/auth/AuthModal";
import GatewayTopNav from "../../components/dashboard/GatewayTopNav";
import GatewaySidebar from "../../components/dashboard/GatewaySidebar";
import { libraryAsset } from "../../utils/libraryAssets";
import { treydoraIpfsCoverToUrl, treydoraNftEntitlementCoverUrl, isUsableDisplayImageUrl, resolveClientV2GenesisTokenCoverPng } from "../../utils/ipfsDisplay";
import { getMyLibrary, getLibraryNftMintChallenge, mintLibraryNftEntitlement } from "../../services/libraryApi";
import { readerPreview as readerPreviewMock, getOwnershipSummary, MY_LIBRARY_HERO_STATS } from "../../data/myLibraryMockData";
import { NOINDEX_PRIVATE_SEO } from "../../constants/siteSeo";
import { usePageSeo } from "../../hooks/usePageSeo";
import CollectorRightsVaultCard from "../../components/legal/CollectorRightsVaultCard";
import "../Dashboard/treydora-gateway-dashboard.css";
import "./my-library.css";

const HERO_BG = libraryAsset("01_library_hero_great_hall.png");
const NFT_VAULT_FALLBACK_COVER = libraryAsset("04_library_featured_display.png");

function shortUserLabel(user) {
  if (!user) return "";
  return user.displayName || user.username || user.email?.split("@")[0] || "Archivist";
}

function formatUsd(n) {
  const x = Number(n);
  if (!Number.isFinite(x) || x < 0) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(x);
}

/** Cover URL for CSS `background-image` (full URL or pack path). */
function coverFromAsset(asset) {
  if (!asset) return libraryAsset("04_library_featured_display.png");
  const c = (asset.coverImage && String(asset.coverImage).trim()) || "";
  const h = (asset.heroImage && String(asset.heroImage).trim()) || "";
  const pick = c || h;
  if (!pick) return libraryAsset("04_library_featured_display.png");
  if (/^https?:\/\//i.test(pick)) return pick;
  if (pick.startsWith("/")) {
    const base = typeof process.env.PUBLIC_URL === "string" ? process.env.PUBLIC_URL : "";
    return `${base}${pick}`;
  }
  return libraryAsset(pick);
}

/**
 * Vault / carousel card cover: token display + metadata image, then edition IPFS hint, then storefront asset covers.
 */
function getLibraryNftCardCoverUrl(row) {
  const meta =
    row?.entitlement?.metadata && typeof row.entitlement.metadata === "object" ? row.entitlement.metadata : {};
  const tokenId = row?.entitlement?.tokenId;
  const metaForV2 = {
    ...meta,
    contractAddress: row?.entitlement?.contractAddress || meta.contractAddress,
  };
  let u = treydoraNftEntitlementCoverUrl(meta);
  if (!isUsableDisplayImageUrl(u)) {
    u = resolveClientV2GenesisTokenCoverPng(metaForV2, tokenId);
  }
  if (isUsableDisplayImageUrl(u)) return u;
  const ed = row?.edition;
  if (ed) {
    const cid = String(ed.ipfsImageCid || "").trim();
    if (cid) {
      const g = treydoraIpfsCoverToUrl(cid);
      if (isUsableDisplayImageUrl(g)) return g;
    }
  }
  return coverFromAsset(row?.asset);
}

function txExplorerUrl(chainId, txHash) {
  const cid = Number(chainId);
  const h = String(txHash || "").trim();
  if (!h || !Number.isFinite(cid)) return null;
  if (cid === 11155111) return `https://sepolia.etherscan.io/tx/${h}`;
  if (cid === 1) return `https://etherscan.io/tx/${h}`;
  if (cid === 5) return `https://goerli.etherscan.io/tx/${h}`;
  return null;
}

/** Sort key: merch_rights → armory → reader_access → other */
function tierPriorityKey(tierKey) {
  const tk = String(tierKey || "").trim();
  if (tk === "merch_rights") return 0;
  if (tk === "armory") return 1;
  if (tk === "reader_access") return 2;
  return 99;
}

/** Compact benefit chips for vault NFT card (tier-driven). */
function buildNftVaultBenefitChips(tierKey) {
  const tk = String(tierKey || "").trim();
  if (tk === "armory") {
    return ["Oshar Relic Cache", "Reader Access", "Treydora & Oshar Access"];
  }
  if (tk === "merch_rights") {
    return ["Merchandising Rights", "Reader Access", "Treydora & Oshar Access"];
  }
  if (tk === "reader_access") {
    return ["Reader Access", "Treydora & Oshar Access"];
  }
  return ["Reader Access", "Treydora & Oshar Access"];
}

function LibraryNftMintControls({ entitlementId, onMintComplete }) {
  const { address, isConnected } = useAccount();
  const { connectAsync, connectors } = useConnect();
  const { chain } = useNetwork();
  const { switchNetwork } = useSwitchNetwork();
  const { signMessageAsync } = useSignMessage();
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState("");
  const [err, setErr] = useState("");
  /** Set after a successful mint-challenge response so we can label “Switch Network” until wagmi chain matches. */
  const [expectedMintChainId, setExpectedMintChainId] = useState(null);

  const currentChainId = chain?.id != null ? Number(chain.id) : null;
  const needsNetworkSwitch =
    Boolean(isConnected && address && expectedMintChainId != null) &&
    Number.isFinite(expectedMintChainId) &&
    expectedMintChainId > 0 &&
    currentChainId !== expectedMintChainId;

  useEffect(() => {
    if (
      expectedMintChainId != null &&
      Number.isFinite(currentChainId) &&
      currentChainId === expectedMintChainId
    ) {
      setExpectedMintChainId(null);
    }
  }, [currentChainId, expectedMintChainId]);

  const runMint = useCallback(async () => {
    setErr("");
    setHint("");
    setBusy(true);
    try {
      let activeAddress = address;
      if (!isConnected || !activeAddress) {
        const connList = Array.isArray(connectors) ? connectors : [];
        const connector = connList.find((c) => c && c.ready) || connList[0];
        if (!connector) {
          throw new Error("No wallet connector available. Install a browser wallet or enable WalletConnect.");
        }
        const connected = await connectAsync({ connector });
        activeAddress = connected?.account;
      }
      if (!activeAddress) {
        throw new Error("Wallet not connected.");
      }

      const challengePayload = await getLibraryNftMintChallenge(entitlementId, activeAddress);
      if (challengePayload?.alreadyMinted) {
        setHint("Already minted — refreshing your library.");
        if (typeof onMintComplete === "function") await onMintComplete();
        return;
      }

      const expectedChain = Number(challengePayload?.chainId);
      if (Number.isFinite(expectedChain) && expectedChain > 0) {
        setExpectedMintChainId(expectedChain);
      }

      const currentId = chain?.id != null ? Number(chain.id) : null;
      if (Number.isFinite(expectedChain) && expectedChain > 0 && currentId !== expectedChain) {
        if (switchNetwork) switchNetwork(expectedChain);
        setHint(
          `This NFT mints on chain ID ${expectedChain}. Approve the network switch in your wallet, then press the button again.`
        );
        return;
      }

      const message = String(challengePayload?.message || "").trim();
      const challengeId = String(challengePayload?.challengeId || "").trim();
      if (!message || !challengeId) {
        throw new Error("Invalid mint challenge from server.");
      }

      const signature = await signMessageAsync({ message });
      await mintLibraryNftEntitlement({
        entitlementId,
        walletAddress: activeAddress,
        signature,
        challengeId,
      });
      setExpectedMintChainId(null);
      setHint("Mint submitted — refreshing your library.");
      if (typeof onMintComplete === "function") await onMintComplete();
    } catch (e) {
      const raw = e?.shortMessage || e?.message || String(e);
      if (String(raw).toLowerCase().includes("user rejected") || String(raw).toLowerCase().includes("denied")) {
        setErr("Signature was cancelled in your wallet.");
      } else {
        setErr(raw || "Mint failed.");
      }
    } finally {
      setBusy(false);
    }
  }, [
    address,
    isConnected,
    connectors,
    connectAsync,
    chain?.id,
    switchNetwork,
    signMessageAsync,
    entitlementId,
    onMintComplete,
  ]);

  const mintButtonLabel = (() => {
    if (!isConnected || !address) {
      if (busy) return "Connecting…";
      return "Connect Wallet to Claim";
    }
    if (needsNetworkSwitch) return "Switch Network";
    if (busy) return "Minting…";
      return "Claim On-Chain Collectible";
  })();

  return (
    <div className="myl-nft-mint-controls myl-nft-vault__mint">
      <p className="myl-nft-mint-controls__lead">
        {!isConnected || !address ? (
          <span className="myl-nft-mint-controls__wallet-muted">Choose the wallet that will hold this NFT on-chain.</span>
        ) : (
          <span>
            Receiving wallet:{" "}
            <span className="myl-nft-mint-controls__wallet">{`${address.slice(0, 6)}…${address.slice(-4)}`}</span>
          </span>
        )}
      </p>
      <button
        type="button"
        className="dg-btn dg-btn--compact myl-nft-mint-btn"
        disabled={busy}
        onClick={runMint}
      >
        {mintButtonLabel}
      </button>
      {hint ? (
        <p className="myl-nft-mint-controls__hint" role="status">
          {hint}
        </p>
      ) : null}
      {err ? (
        <p className="myl-nft-mint-controls__err" role="alert">
          {err}
        </p>
      ) : null}
    </div>
  );
}

export default function MyLibraryPage() {
  const { user, loading, isAuthenticated } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [vaultItems, setVaultItems] = useState(null);
  const [nftFilter, setNftFilter] = useState("all");
  const [nftSort, setNftSort] = useState("newest");
  /** Full sortable inventory list below carousel — closed by default for a cleaner vault. */
  const [showInventoryList, setShowInventoryList] = useState(false);
  const [nftDrawerId, setNftDrawerId] = useState(null);
  const navigate = useNavigate();

  const refreshVault = useCallback(async () => {
    try {
      const data = await getMyLibrary();
      setVaultItems(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setVaultItems([]);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setVaultItems(null);
      return undefined;
    }
    let cancelled = false;
    setVaultItems(null);
    (async () => {
      try {
        const data = await getMyLibrary();
        if (!cancelled) setVaultItems(Array.isArray(data?.items) ? data.items : []);
      } catch {
        if (!cancelled) setVaultItems([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const nftRows = useMemo(() => {
    if (!Array.isArray(vaultItems)) return [];
    return vaultItems
      .filter((row) => row?.edition?.editionType === "nft")
      .map((row) => {
        const meta =
          row.entitlement?.metadata && typeof row.entitlement.metadata === "object" ? row.entitlement.metadata : {};
        const mintStatus = String(meta.mintStatus || "").trim();
        const assigned =
          meta.assignedTokenId != null && meta.assignedTokenId !== ""
            ? Number(meta.assignedTokenId)
            : null;
        const assignedOk = Number.isFinite(assigned);
        const tokenIdRaw = row.entitlement?.tokenId;
        const hasOnChainToken = tokenIdRaw != null && String(tokenIdRaw).trim() !== "";
        let tokenDisplay = "Pending mint";
        if (mintStatus === "minted" && hasOnChainToken) {
          tokenDisplay = `#${tokenIdRaw}`;
        } else if (assignedOk) {
          tokenDisplay = `#${assigned} (assigned)`;
        } else if (mintStatus === "paid_unminted") {
          tokenDisplay = "Pending mint";
        } else if (hasOnChainToken) {
          tokenDisplay = `#${tokenIdRaw}`;
        }
        const tierKey = row.edition?.tierKey || "";
        const nftCoverUrl = getLibraryNftCardCoverUrl(row);
        const ent = row.entitlement || {};
        const txH = String(meta.txHash || ent.txHash || "").trim();
        const chainId = meta.chainId != null ? Number(meta.chainId) : null;
        const mintedWallet = String(meta.walletAddress || ent.walletAddress || "").trim();
        const mintedExplorerUrl = txExplorerUrl(chainId, txH);
        const contractAddr =
          String(ent.contractAddress || meta.contractAddress || row.edition?.contractAddress || "").trim() || "—";
        const mintStatusHeadline =
          mintStatus === "paid_unminted"
            ? "Claim Pending"
            : mintStatus === "minted"
              ? "Minted"
              : /fail/i.test(String(mintStatus || ""))
                ? "Mint Failed"
                : mintStatus || "—";
        const pendingMintSubtext =
          mintStatus === "paid_unminted" && assignedOk
            ? `Card purchase complete. Connect a wallet to claim on-chain collectible #${assigned}.`
            : mintStatus === "paid_unminted"
              ? "Card purchase complete. Connect a wallet to claim your on-chain collectible."
              : null;
        const benefitChips = buildNftVaultBenefitChips(tierKey);
        const tokenBadge =
          assignedOk ? `#${assigned}` : hasOnChainToken ? `#${String(tokenIdRaw).replace(/^#/, "")}` : "—";
        const grantedAtRaw = row.entitlement?.grantedAt;
        const grantedAtMs = grantedAtRaw ? new Date(grantedAtRaw).getTime() : 0;
        const assignedSort =
          assignedOk && Number.isFinite(assigned)
            ? assigned
            : hasOnChainToken && Number.isFinite(Number(tokenIdRaw))
              ? Number(tokenIdRaw)
              : 99999999;
        const imageUrl = String(meta.imageUrl || "").trim();
        const metadataUrl = String(meta.metadataUrl || "").trim();
        const orderId = String(row.entitlement?.orderId || "").trim();
        const entId = String(row.entitlement?.id || "").trim();
        const purchaseMethod = String(meta.purchaseMethod || "").trim();
        return {
          key: entId ? `nft-ent-${entId}` : `${row.entitlement?.id || ""}-${row.asset?.slug || ""}`,
          entitlementId: entId,
          assetSlug: row.asset?.slug || "",
          title: row.asset?.title || "Library item",
          editionName: row.edition?.title || "",
          tierKey,
          tokenId: tokenDisplay,
          contractAddress: contractAddr,
          accessSummary: row.edition?.accessSummary || row.edition?.rightsSummary || "",
          benefits: Array.isArray(row.edition?.benefits) ? row.edition.benefits : [],
          status: row.entitlement?.status || "active",
          coverUrl: nftCoverUrl,
          mintStatus,
          mintStatusHeadline,
          mintStatusLabel: mintStatusHeadline,
          pendingMintSubtext,
          benefitChips,
          tokenBadge,
          mintedTxHash: txH,
          mintedChainId: Number.isFinite(chainId) ? chainId : null,
          mintedExplorerUrl,
          mintedWallet,
          grantedAt: grantedAtRaw ? new Date(grantedAtRaw).toISOString() : "",
          grantedAtMs,
          assignedSort,
          imageUrl,
          metadataUrl,
          orderId,
          purchaseMethod,
          purchaseMethodLabel:
            purchaseMethod === "eth_wallet"
              ? "Paid with ETH (Sepolia)"
              : purchaseMethod === "stripe_card"
                ? "Digital Collector Edition (card)"
                : "",
        };
      });
  }, [vaultItems]);

  const digitalRows = useMemo(() => {
    if (!Array.isArray(vaultItems)) return [];
    return vaultItems
      .filter((row) => row?.edition?.editionType === "digital")
      .map((row) => {
        const asset = row.asset;
        const edition = row.edition;
        const isBook = edition?.tierKey === "digital_book";
        return {
          key: `${row.entitlement?.id || ""}-${asset?.slug || ""}-${edition?.slug || ""}`,
          assetSlug: asset?.slug || "",
          title: asset?.title || "Library item",
          format: `${edition?.title || "Digital"} · ${isBook ? "eBook · Novel" : "Digital · Treydora viewer"}`,
          description: asset?.shortDescription || asset?.description || edition?.accessSummary || "",
          pricePaid: formatUsd(edition?.priceUsd),
          accessStatus: row.entitlement?.source === "stripe" ? "Active · Stripe" : "Active",
          coverUrl: coverFromAsset(asset),
        };
      });
  }, [vaultItems]);

  const nftCollectionStats = useMemo(() => {
    const total = nftRows.length;
    let pending = 0;
    let minted = 0;
    const tiers = { merch_rights: 0, armory: 0, reader_access: 0 };
    for (const n of nftRows) {
      if (n.mintStatus === "paid_unminted") pending += 1;
      else if (n.mintStatus === "minted") minted += 1;
      const tk = n.tierKey;
      if (Object.prototype.hasOwnProperty.call(tiers, tk)) tiers[tk] += 1;
    }
    return { total, pending, minted, tiers };
  }, [nftRows]);

  const featuredNfts = useMemo(() => {
    const copy = [...nftRows];
    copy.sort((a, b) => {
      const ta = tierPriorityKey(a.tierKey);
      const tb = tierPriorityKey(b.tierKey);
      if (ta !== tb) return ta - tb;
      const pa = a.mintStatus === "paid_unminted" ? 0 : 1;
      const pb = b.mintStatus === "paid_unminted" ? 0 : 1;
      if (pa !== pb) return pa - pb;
      return (b.grantedAtMs || 0) - (a.grantedAtMs || 0);
    });
    return copy;
  }, [nftRows]);

  const filteredNftCollection = useMemo(() => {
    let list = [...nftRows];
    if (nftFilter === "pending") list = list.filter((n) => n.mintStatus === "paid_unminted");
    else if (nftFilter === "minted") list = list.filter((n) => n.mintStatus === "minted");
    else if (["merch_rights", "armory", "reader_access"].includes(nftFilter)) {
      list = list.filter((n) => n.tierKey === nftFilter);
    }
    if (nftSort === "newest") {
      list.sort((a, b) => (b.grantedAtMs || 0) - (a.grantedAtMs || 0));
    } else if (nftSort === "token") {
      list.sort((a, b) => (a.assignedSort || 0) - (b.assignedSort || 0));
    } else if (nftSort === "edition") {
      list.sort(
        (a, b) =>
          tierPriorityKey(a.tierKey) - tierPriorityKey(b.tierKey) ||
          String(a.editionName || "").localeCompare(String(b.editionName || ""))
      );
    } else if (nftSort === "status") {
      list.sort((a, b) => String(a.mintStatus || "").localeCompare(String(b.mintStatus || "")));
    }
    return list;
  }, [nftRows, nftFilter, nftSort]);

  const primaryReaderNft = useMemo(() => featuredNfts[0] || null, [featuredNfts]);

  const summary = useMemo(() => getOwnershipSummary(nftRows, digitalRows), [nftRows, digitalRows]);

  const vaultLoading = isAuthenticated && vaultItems === null;
  const hasVaultItems = Array.isArray(vaultItems) && vaultItems.length > 0;

  const readerPreview = useMemo(() => {
    const interior = readerPreviewMock.interiorPreviewImage
      ? libraryAsset(readerPreviewMock.interiorPreviewImage)
      : "";
    const storyteller = readerPreviewMock.storytellerImageFile
      ? libraryAsset(readerPreviewMock.storytellerImageFile)
      : "";
    if (primaryReaderNft) {
      return {
        assetSlug: primaryReaderNft.assetSlug || readerPreviewMock.assetSlug,
        nftCoverUrl: primaryReaderNft.coverUrl || "",
        interiorUrl: interior,
        storytellerUrl: storyteller,
        progress: readerPreviewMock.progress,
      };
    }
    const first = Array.isArray(vaultItems) && vaultItems.length ? vaultItems[0] : null;
    const asset = first?.asset;
    if (!asset) {
      return {
        assetSlug: readerPreviewMock.assetSlug,
        nftCoverUrl: readerPreviewMock.nftCoverImage ? libraryAsset(readerPreviewMock.nftCoverImage) : "",
        interiorUrl: interior,
        storytellerUrl: storyteller,
        progress: readerPreviewMock.progress,
      };
    }
    return {
      assetSlug: asset.slug || readerPreviewMock.assetSlug,
      nftCoverUrl: coverFromAsset(asset),
      interiorUrl: interior,
      storytellerUrl: storyteller,
      progress: readerPreviewMock.progress,
    };
  }, [vaultItems, primaryReaderNft]);

  const heroStats = useMemo(
    () =>
      MY_LIBRARY_HERO_STATS(
        summary.nftComics,
        summary.digitalEditions,
        summary.readingAccess === "Active",
        summary.loreAccess
      ),
    [summary]
  );

  const drawerNft = useMemo(() => {
    if (!nftDrawerId) return null;
    return nftRows.find((n) => n.entitlementId === nftDrawerId) || null;
  }, [nftRows, nftDrawerId]);

  useEffect(() => {
    if (!nftDrawerId) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setNftDrawerId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nftDrawerId]);

  usePageSeo({
    ...NOINDEX_PRIVATE_SEO,
    title: "My Treydora Library | Treydora",
    description: "Your owned comics, digital editions, and archive access.",
  });

  if (loading) {
    return (
      <div className="dashboard-shell dashboard-shell--loading">
        <p className="dg-hud">Opening your archive…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="dashboard-shell dashboard-shell--gateway">
        <div
          className="myl-gate myl-gate--logged-out dg-gold-ring"
          style={{ "--myl-hero-bg": `url(${HERO_BG})` }}
        >
          <div className="myl-gate__bg" aria-hidden />
          <div className="myl-gate__vignette" aria-hidden />
          <div className="myl-gate__inner">
            <span className="myl-eyebrow">Personal Archive</span>
            <h1 className="myl-gate__title serif">Log in to view your Treydora Library.</h1>
            <p className="myl-gate__lead">
              Your vault of owned NFT editions, digital comics, and unlocked lore opens after authentication.
            </p>
            <button type="button" className="dg-btn dg-btn--primary" onClick={() => setAuthOpen(true)}>
              Log In to View Library
            </button>
            <p className="myl-gate__hint">
              <Link to="/library">Browse the public Library storefront</Link>
            </p>
          </div>
        </div>
        <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
      </div>
    );
  }

  const readerPath =
    readerPreview.assetSlug && (primaryReaderNft || hasVaultItems)
      ? primaryReaderNft && primaryReaderNft.entitlementId
        ? `/library/read/${encodeURIComponent(primaryReaderNft.assetSlug)}?entitlementId=${encodeURIComponent(
            primaryReaderNft.entitlementId
          )}`
        : `/library/read/${encodeURIComponent(readerPreview.assetSlug)}`
      : "/library/read/treydora-genesis-comic";

  return (
    <div className="dashboard-shell dashboard-shell--gateway">
      <GatewayTopNav />
      <div className="dashboard-body dg-gateway-atmosphere">
        <GatewaySidebar />
        <div className="dashboard-workpad">
          <main className="myl myl-vault-dash" aria-label="My Treydora Library">
            <div className="myl-layout">
              <div className="myl-main">
            <header
              className="myl-hero myl-hero--epic dg-gold-ring"
              style={{ "--myl-hero-bg": `url(${HERO_BG})` }}
            >
              <div className="myl-hero__bg" aria-hidden />
              <div className="myl-hero__vignette" aria-hidden />
              <div className="myl-hero__inner">
                <span className="myl-eyebrow">Personal Archive</span>
                <h1 className="myl-hero__title serif">My Treydora Library</h1>
                <p className="myl-hero__subtitle">
                  Your owned stories, comic relics, digital editions, and unlocked lore.
                </p>
                <div className="myl-hero__identity">
                  <span className="myl-hero__user">{shortUserLabel(user)}</span>
                  <span className="myl-hero__sep" aria-hidden>
                    ·
                  </span>
                  <span className="myl-hero__status">
                    Vault status: {vaultLoading ? "Syncing…" : hasVaultItems ? "Sealed & stocked" : "Awaiting relics"}
                  </span>
                  <span className="myl-hero__sep" aria-hidden>
                    ·
                  </span>
                  <span className="myl-hero__count">{summary.nftComics + summary.digitalEditions} owned items</span>
                </div>
                <div className="myl-hero__chips" role="list">
                  {heroStats.map((s) => (
                    <div key={s.key} className="myl-stat-chip" role="listitem">
                      <span className="myl-stat-chip__label">{s.label}</span>
                      <span className="myl-stat-chip__val">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </header>

            <section className="myl-section myl-archive-metrics" aria-labelledby="myl-summary-heading">
              <h2 id="myl-summary-heading" className="myl-section-title sr-only">
                Ownership summary
              </h2>
              <div className="myl-summary-grid">
                <article className="myl-summary-card dg-gold-ring">
                  <span className="myl-summary-card__label">Collector Editions</span>
                  <span className="myl-summary-card__val">{summary.nftComics}</span>
                  <span className="myl-summary-card__hint">On-chain editions</span>
                </article>
                <article className="myl-summary-card dg-gold-ring">
                  <span className="myl-summary-card__label">Digital Editions</span>
                  <span className="myl-summary-card__val">{summary.digitalEditions}</span>
                  <span className="myl-summary-card__hint">Comics & books</span>
                </article>
                <article className="myl-summary-card dg-gold-ring">
                  <span className="myl-summary-card__label">Reading Access</span>
                  <span className="myl-summary-card__val">{summary.readingAccess}</span>
                  <span className="myl-summary-card__hint">Treydora viewer</span>
                </article>
                <article className="myl-summary-card dg-gold-ring">
                  <span className="myl-summary-card__label">AI Storyteller</span>
                  <span className="myl-summary-card__val">{summary.loreAccess}</span>
                  <span className="myl-summary-card__hint">Owner depth</span>
                </article>
              </div>
            </section>

            <section className="myl-section myl-oshar-note" aria-label="Oshar VR real estate access">
              <p className="myl-section-lead" style={{ marginTop: 4, marginBottom: 0 }}>
                <strong>Oshar VR real estate</strong> (Stripe card checkout) is tracked as access deeds on your{" "}
                <Link to="/dashboard#oshar-access">Gateway dashboard</Link> — separate from comic Collector Vault
                entitlements.
              </p>
            </section>

            {vaultLoading ? (
              <p className="myl-section-lead" style={{ marginTop: 8 }}>
                Loading your vault from Treydora…
              </p>
            ) : null}

            {!vaultLoading && !hasVaultItems ? (
              <section className="myl-empty dg-gold-ring" aria-labelledby="myl-empty-title">
                <h2 id="myl-empty-title" className="myl-empty__title serif">
                  Your Library Is Waiting
                </h2>
                <p className="myl-empty__text">
                  Purchase a comic, digital edition, or NFT-bound story to begin building your Treydora archive.
                </p>
                <div className="myl-empty__actions">
                  <Link to="/library" className="dg-btn dg-btn--primary">
                    Browse Library
                  </Link>
                  <Link to="/library#store-nft" className="dg-btn dg-btn--blue-outline">
                    Explore Collector Editions
                  </Link>
                </div>
              </section>
            ) : null}

            {!vaultLoading && nftRows.length > 0 ? (
              <>
                <section className="myl-section myl-nft-vault-root myl-vault-arena" aria-labelledby="myl-nft-vault-heading">
                  <div className="myl-section-head myl-vault-arena__head">
                    <h2 id="myl-nft-vault-heading" className="myl-section-title serif myl-section-title--vault">
                      Treydora Collector Vault
                    </h2>
                    <p className="myl-section-lead">
                      Each purchase is its own entitlement: unique cover, token slot, and mint state. The same
                      protected comic opens in the Treydora reader with the Lore Terminal.
                    </p>
                  </div>

                  <article className="myl-nft-summary myl-nft-summary--dramatic dg-gold-ring" aria-label="NFT collection summary">
                    <div className="myl-nft-summary__main">
                      <div className="myl-nft-summary__counts">
                        <div>
                          <span className="myl-nft-summary__n">{nftCollectionStats.total}</span>
                          <span className="myl-nft-summary__lbl">Editions owned</span>
                        </div>
                        <div>
                          <span className="myl-nft-summary__n">{nftCollectionStats.pending}</span>
                          <span className="myl-nft-summary__lbl">Claim pending</span>
                        </div>
                        <div>
                          <span className="myl-nft-summary__n">{nftCollectionStats.minted}</span>
                          <span className="myl-nft-summary__lbl">Minted</span>
                        </div>
                      </div>
                      <div className="myl-nft-summary__tiers" aria-label="Count by tier">
                        <span>Merch Rights · {nftCollectionStats.tiers.merch_rights}</span>
                        <span>Armory · {nftCollectionStats.tiers.armory}</span>
                        <span>Reader Access · {nftCollectionStats.tiers.reader_access}</span>
                      </div>
                    </div>
                    <div className="myl-nft-summary__actions">
                      <button
                        type="button"
                        className="dg-btn dg-btn--gold-outline dg-btn--compact"
                        onClick={() => navigate(readerPath)}
                      >
                        Open Comic
                      </button>
                      <Link to="/library#store-nft" className="dg-btn dg-btn--primary dg-btn--compact">
                        Buy More Editions
                      </Link>
                    </div>
                  </article>

                  <div className="myl-nft-carousel-wrap">
                    <h3 className="myl-nft-subheading myl-nft-subheading--spotlight">Featured in your vault</h3>
                    <div className="myl-nft-carousel" role="region" aria-label="Featured NFT covers">
                      <div className="myl-nft-carousel__track">
                        {featuredNfts.map((nft) => (
                          <article key={`car-${nft.key}`} className="myl-nft-carousel__slide dg-gold-ring">
                            <div className="myl-nft-carousel__media">
                              <img
                                className="myl-nft-carousel__img"
                                src={nft.coverUrl || NFT_VAULT_FALLBACK_COVER}
                                alt=""
                                loading="lazy"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = NFT_VAULT_FALLBACK_COVER;
                                }}
                              />
                              <div className="myl-nft-carousel__scrim" aria-hidden />
                              <span
                                className={`myl-nft-carousel__pill myl-nft-carousel__pill--${
                                  nft.mintStatus === "minted" ? "minted" : "pending"
                                }`}
                              >
                                {nft.mintStatusHeadline}
                              </span>
                              <span className="myl-nft-carousel__pill myl-nft-carousel__pill--tier">
                                {nft.editionName}
                              </span>
                              <span className="myl-nft-carousel__pill myl-nft-carousel__pill--token">
                                {nft.tokenBadge}
                              </span>
                            </div>
                            <div className="myl-nft-carousel__body">
                              <h4 className="myl-nft-carousel__title serif">{nft.title}</h4>
                              <div className="myl-nft-carousel__actions">
                                <button
                                  type="button"
                                  className="dg-btn dg-btn--gold-outline dg-btn--compact"
                                  onClick={() =>
                                    navigate(
                                      nft.entitlementId
                                        ? `/library/read/${encodeURIComponent(
                                            nft.assetSlug
                                          )}?entitlementId=${encodeURIComponent(nft.entitlementId)}`
                                        : `/library/read/${encodeURIComponent(nft.assetSlug)}`
                                    )
                                  }
                                >
                                  Open Comic
                                </button>
                                <button
                                  type="button"
                                  className="dg-btn dg-btn--ghost dg-btn--compact"
                                  onClick={() => setNftDrawerId(nft.entitlementId)}
                                >
                                  Details
                                </button>
                                {nft.mintStatus === "paid_unminted" && nft.entitlementId ? (
                                  <button
                                    type="button"
                                    className="dg-btn dg-btn--compact myl-nft-mint-btn"
                                    onClick={() => setNftDrawerId(nft.entitlementId)}
                                  >
                                    Claim On-Chain Collectible
                                  </button>
                                ) : null}
                                {nft.mintStatus === "minted" && nft.mintedExplorerUrl ? (
                                  <a
                                    className="dg-btn dg-btn--blue-outline dg-btn--compact"
                                    href={nft.mintedExplorerUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    View on Explorer
                                  </a>
                                ) : null}
                              </div>
                            </div>
                          </article>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="myl-nft-toolbar">
                    <div className="myl-nft-toolbar__filters" role="group" aria-label="Filter NFT collection">
                      {[
                        { id: "all", label: "All" },
                        { id: "pending", label: "Claim Pending" },
                        { id: "minted", label: "Minted" },
                        { id: "reader_access", label: "Reader Access" },
                        { id: "armory", label: "Armory" },
                        { id: "merch_rights", label: "Merch Rights" },
                      ].map((f) => (
                        <button
                          key={f.id}
                          type="button"
                          className={`myl-nft-pill${nftFilter === f.id ? " is-on" : ""}`}
                          onClick={() => setNftFilter(f.id)}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                    <div className="myl-nft-toolbar__right">
                      <label className="myl-nft-toolbar__sort">
                        <span className="myl-nft-toolbar__sort-label">Sort</span>
                        <select
                          className="myl-nft-toolbar__select"
                          value={nftSort}
                          onChange={(e) => setNftSort(e.target.value)}
                        >
                          <option value="newest">Newest</option>
                          <option value="token">Token ID</option>
                          <option value="edition">Edition</option>
                          <option value="status">Mint status</option>
                        </select>
                      </label>
                      <button
                        type="button"
                        className="myl-nft-inventory-toggle"
                        aria-expanded={showInventoryList}
                        aria-controls="myl-nft-inventory-panel"
                        onClick={() => setShowInventoryList((v) => !v)}
                      >
                        {showInventoryList ? "Hide Inventory List" : "Show Inventory List"}
                      </button>
                    </div>
                  </div>

                  {showInventoryList ? (
                    <div
                      id="myl-nft-inventory-panel"
                      className="myl-nft-inventory-panel"
                      role="region"
                      aria-label="NFT inventory list"
                    >
                      <ul className="myl-nft-collection-grid">
                    {filteredNftCollection.map((nft) => (
                      <li key={nft.key} className="myl-nft-collect-row dg-gold-ring">
                        <div className="myl-nft-collect-row__thumb" role="presentation">
                          <img
                            className="myl-nft-collect-row__thumbImg"
                            src={nft.coverUrl || NFT_VAULT_FALLBACK_COVER}
                            alt=""
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = NFT_VAULT_FALLBACK_COVER;
                            }}
                          />
                        </div>
                        <div className="myl-nft-collect-row__meta">
                          <span className="myl-nft-collect-row__edition">{nft.editionName}</span>
                          <span className="myl-nft-collect-row__token">{nft.tokenBadge}</span>
                          <span
                            className={`myl-nft-collect-row__status myl-nft-collect-row__status--${
                              nft.mintStatus === "minted" ? "minted" : "pending"
                            }`}
                          >
                            {nft.mintStatusHeadline}
                          </span>
                          {nft.mintStatus === "minted" && nft.mintedTxHash ? (
                            <span className="myl-nft-collect-row__txhint" title={nft.mintedTxHash}>
                              Tx on file
                            </span>
                          ) : null}
                        </div>
                        <div className="myl-nft-collect-row__actions">
                          <button
                            type="button"
                            className="dg-btn dg-btn--ghost dg-btn--compact"
                            onClick={() =>
                              navigate(
                                nft.entitlementId
                                  ? `/library/read/${encodeURIComponent(nft.assetSlug)}?entitlementId=${encodeURIComponent(
                                      nft.entitlementId
                                    )}`
                                  : `/library/read/${encodeURIComponent(nft.assetSlug)}`
                              )
                            }
                          >
                            Open Comic
                          </button>
                          <button
                            type="button"
                            className="dg-btn dg-btn--blue-outline dg-btn--compact"
                            onClick={() => setNftDrawerId(nft.entitlementId)}
                          >
                            Details
                          </button>
                          {nft.mintStatus === "paid_unminted" && nft.entitlementId ? (
                            <button
                              type="button"
                              className="dg-btn dg-btn--compact myl-nft-mint-btn"
                              onClick={() => setNftDrawerId(nft.entitlementId)}
                            >
                              Claim On-Chain Collectible
                            </button>
                          ) : null}
                          {nft.mintStatus === "minted" && nft.mintedExplorerUrl ? (
                            <a
                              className="dg-btn dg-btn--blue-outline dg-btn--compact"
                              href={nft.mintedExplorerUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Explorer
                            </a>
                          ) : null}
                        </div>
                      </li>
                    ))}
                      </ul>
                    </div>
                  ) : null}
                </section>

                {drawerNft ? (
                  <div
                    className="myl-nft-drawer-overlay"
                    role="presentation"
                    onClick={() => setNftDrawerId(null)}
                  >
                    <div
                      className="myl-nft-drawer dg-gold-ring"
                      role="dialog"
                      aria-modal="true"
                      aria-labelledby="myl-nft-drawer-title"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="myl-nft-drawer__close"
                        aria-label="Close details"
                        onClick={() => setNftDrawerId(null)}
                      >
                        ×
                      </button>
                      <div className="myl-nft-drawer__hero">
                        <img
                          className="myl-nft-drawer__cover"
                          src={drawerNft.coverUrl || NFT_VAULT_FALLBACK_COVER}
                          alt=""
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = NFT_VAULT_FALLBACK_COVER;
                          }}
                        />
                      </div>
                      <div className="myl-nft-drawer__body">
                        <h3 id="myl-nft-drawer-title" className="myl-nft-drawer__title serif">
                          {drawerNft.title}
                        </h3>
                        <p className="myl-nft-drawer__line">{drawerNft.editionName}</p>
                        <dl className="myl-nft-drawer__dl">
                          <div>
                            <dt>Token</dt>
                            <dd>{drawerNft.tokenBadge}</dd>
                          </div>
                          <div>
                            <dt>On-chain status</dt>
                            <dd>{drawerNft.mintStatusHeadline}</dd>
                          </div>
                          {drawerNft.grantedAt ? (
                            <div>
                              <dt>Purchase date</dt>
                              <dd>{new Date(drawerNft.grantedAt).toLocaleString()}</dd>
                            </div>
                          ) : null}
                          {drawerNft.mintedChainId ? (
                            <div>
                              <dt>Network</dt>
                              <dd>
                                {drawerNft.mintedChainId === 11155111
                                  ? "Sepolia (11155111)"
                                  : `Chain ${drawerNft.mintedChainId}`}
                              </dd>
                            </div>
                          ) : null}
                          {drawerNft.metadataUrl ? (
                            <div className="myl-nft-drawer__dl-wide">
                              <dt>Metadata</dt>
                              <dd>
                                <a href={drawerNft.metadataUrl} target="_blank" rel="noreferrer">
                                  View metadata URI
                                </a>
                              </dd>
                            </div>
                          ) : null}
                          {drawerNft.mintedTxHash ? (
                            <div className="myl-nft-drawer__dl-wide">
                              <dt>Transaction</dt>
                              <dd>
                                <code className="myl-nft-drawer__code">{drawerNft.mintedTxHash}</code>
                              </dd>
                            </div>
                          ) : null}
                          {drawerNft.mintedWallet ? (
                            <div className="myl-nft-drawer__dl-wide">
                              <dt>Wallet</dt>
                              <dd className="myl-nft-drawer__code">{drawerNft.mintedWallet}</dd>
                            </div>
                          ) : null}
                          {drawerNft.purchaseMethodLabel ? (
                            <div className="myl-nft-drawer__dl-wide">
                              <dt>Purchase</dt>
                              <dd>{drawerNft.purchaseMethodLabel}</dd>
                            </div>
                          ) : null}
                        </dl>
                        <div className="myl-nft-drawer__actions">
                          <button
                            type="button"
                            className="dg-btn dg-btn--gold-outline dg-btn--compact"
                            onClick={() =>
                              navigate(
                                drawerNft.entitlementId
                                  ? `/library/read/${encodeURIComponent(
                                      drawerNft.assetSlug
                                    )}?entitlementId=${encodeURIComponent(drawerNft.entitlementId)}`
                                  : `/library/read/${encodeURIComponent(drawerNft.assetSlug)}`
                              )
                            }
                          >
                            Open Comic
                          </button>
                          <Link to="/library#store-nft" className="dg-btn dg-btn--primary dg-btn--compact">
                            Buy Another Edition
                          </Link>
                          {drawerNft.mintStatus === "minted" && drawerNft.mintedExplorerUrl ? (
                            <a
                              className="dg-btn dg-btn--blue-outline dg-btn--compact"
                              href={drawerNft.mintedExplorerUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View on Explorer
                            </a>
                          ) : null}
                        </div>
                        {drawerNft.mintStatus === "paid_unminted" && drawerNft.entitlementId ? (
                          <div className="myl-nft-drawer__mint">
                            <LibraryNftMintControls
                              entitlementId={drawerNft.entitlementId}
                              onMintComplete={async () => {
                                await refreshVault();
                                setNftDrawerId(null);
                              }}
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}
              </>
            ) : null}

            {!vaultLoading && digitalRows.length > 0 ? (
              <section className="myl-section" aria-labelledby="myl-digital-heading">
                <div className="myl-section-head">
                  <h2 id="myl-digital-heading" className="myl-section-title serif">
                    My Digital Editions
                  </h2>
                  <p className="myl-section-lead">Purchased digital products — no NFT required.</p>
                </div>
                <div className="myl-digital-grid">
                  {digitalRows.map((d) => (
                    <article key={d.key} className="myl-digital-card dg-gold-ring">
                      <div
                        className="myl-digital-card__cover"
                        style={{ backgroundImage: `url(${d.coverUrl})` }}
                        role="img"
                        aria-hidden
                      />
                      <div className="myl-digital-card__body">
                        <span className="myl-digital-card__format">{d.format}</span>
                        <h3 className="myl-digital-card__title">{d.title}</h3>
                        <p className="myl-digital-card__desc">{d.description}</p>
                        <p className="myl-digital-card__meta">
                          Paid {d.pricePaid} · <span className="myl-digital-card__status">{d.accessStatus}</span>
                        </p>
                        <button
                          type="button"
                          className="dg-btn dg-btn--primary dg-btn--compact"
                          onClick={() => navigate(`/library/read/${d.assetSlug}`)}
                        >
                          Open Viewer / Reader
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            ) : null}

            {!vaultLoading && hasVaultItems ? (
              <section className="myl-section myl-section--continue" aria-labelledby="myl-continue-heading">
                <h2 id="myl-continue-heading" className="myl-section-title serif">
                  Continue Reading
                </h2>
                <p className="myl-section-lead">
                  Matches the reader: NFT cover, interior, and Storyteller card — open the viewer for the full AI panel.
                </p>
                <div className="myl-continue-panel dg-gold-ring">
                  <div className="myl-continue__triptych">
                    <div className="myl-continue__pane myl-continue__pane--cover">
                      <span className="myl-continue__label">NFT cover</span>
                      <div
                        className="myl-continue__thumb"
                        style={{
                          backgroundImage: readerPreview.nftCoverUrl
                            ? `url(${readerPreview.nftCoverUrl})`
                            : undefined,
                        }}
                      />
                    </div>
                    <div className="myl-continue__pane myl-continue__pane--center">
                      <span className="myl-continue__label">Interior</span>
                      <div
                        className="myl-continue__thumb myl-continue__thumb--interior"
                        style={{
                          backgroundImage: readerPreview.interiorUrl
                            ? `url(${readerPreview.interiorUrl})`
                            : undefined,
                        }}
                      />
                      <span className="myl-continue__progress">{readerPreview.progress}</span>
                    </div>
                    <div className="myl-continue__pane myl-continue__pane--cover">
                      <span className="myl-continue__label">Storyteller</span>
                      <div
                        className="myl-continue__thumb myl-continue__thumb--story"
                        style={{
                          backgroundImage: readerPreview.storytellerUrl
                            ? `url(${readerPreview.storytellerUrl})`
                            : undefined,
                        }}
                      />
                    </div>
                  </div>
                  <button type="button" className="dg-btn dg-btn--gold-outline" onClick={() => navigate(readerPath)}>
                    Open Comic Viewer
                  </button>
                </div>
              </section>
            ) : null}

            <section className="myl-section myl-wings" aria-labelledby="myl-wings-heading">
              <div className="myl-section-head">
                <h2 id="myl-wings-heading" className="myl-section-title serif myl-section-title--wings">
                  Archive Wings
                </h2>
                <p className="myl-section-lead">
                  Cross the threshold into storefront wings, lore chronicles, and the protected reader.
                </p>
              </div>
              <nav className="myl-wings__grid" aria-label="Library archive wings">
                <Link className="myl-wing-tile dg-gold-ring" to="/library#store-nft">
                  <span className="myl-wing-tile__label">Comics</span>
                  <span className="myl-wing-tile__hint">Collector editions &amp; NFT tiers</span>
                </Link>
                <Link className="myl-wing-tile dg-gold-ring" to="/library#store-digital">
                  <span className="myl-wing-tile__label">Books</span>
                  <span className="myl-wing-tile__hint">Digital novels &amp; viewer editions</span>
                </Link>
                <Link className="myl-wing-tile dg-gold-ring" to="/chronicles">
                  <span className="myl-wing-tile__label">Lore Fragments</span>
                  <span className="myl-wing-tile__hint">Chronicles &amp; world canon</span>
                </Link>
                <Link className="myl-wing-tile dg-gold-ring" to="/library#store-nft">
                  <span className="myl-wing-tile__label">Access Relics</span>
                  <span className="myl-wing-tile__hint">Vault-grade storefront keys</span>
                </Link>
                <Link className="myl-wing-tile dg-gold-ring" to={readerPath}>
                  <span className="myl-wing-tile__label">Storyteller Mode</span>
                  <span className="myl-wing-tile__hint">Open the lore terminal</span>
                </Link>
              </nav>
            </section>

            <p className="myl-footnote">
              <Link to="/library">Public Library storefront</Link>
              <span aria-hidden> · </span>
              Vault syncs from Treydora Library (Stripe: digital editions and NFT mint-later tiers via webhook).
            </p>
              </div>

              <aside className="myl-rail" aria-label="Archive status and quick actions">
                <div className="myl-rpanel myl-rpanel--story dg-gold-ring">
                  <h2 className="myl-rpanel__title">Storyteller preview</h2>
                  <div className="myl-rpanel__visual">
                    <img src={libraryAsset("03_ai_storyteller_oracle.png")} alt="" loading="lazy" />
                  </div>
                  <p className="myl-rpanel__copy">
                    Your reader pairs cover art, interior spreads, and the AI oracle lane — same flow as Continue
                    Reading.
                  </p>
                  <Link to={readerPath} className="dg-btn dg-btn--gold-outline dg-btn--compact">
                    Open comic reader
                  </Link>
                </div>

                <CollectorRightsVaultCard />

                <div className="myl-rpanel dg-gold-ring">
                  <h2 className="myl-rpanel__title">Archive status</h2>
                  <p className="myl-rpanel__stat">
                    <span className="myl-rpanel__stat-label">Vault</span>
                    <span className="myl-rpanel__stat-value">
                      {vaultLoading ? "Syncing…" : hasVaultItems ? "Sealed & stocked" : "Awaiting relics"}
                    </span>
                  </p>
                  <p className="myl-rpanel__stat">
                    <span className="myl-rpanel__stat-label">Owned items</span>
                    <span className="myl-rpanel__stat-value">{summary.nftComics + summary.digitalEditions}</span>
                  </p>
                  <p className="myl-rpanel__stat">
                    <span className="myl-rpanel__stat-label">Reading access</span>
                    <span className="myl-rpanel__stat-value">{summary.readingAccess}</span>
                  </p>
                </div>

                <div className="myl-rpanel dg-gold-ring">
                  <h2 className="myl-rpanel__title">Vault snapshot</h2>
                  {nftRows.length > 0 ? (
                    <ul className="myl-rpanel__list">
                      <li>
                        <span>Editions</span> <strong>{nftCollectionStats.total}</strong>
                      </li>
                      <li>
                        <span>Claim pending</span> <strong>{nftCollectionStats.pending}</strong>
                      </li>
                      <li>
                        <span>Minted</span> <strong>{nftCollectionStats.minted}</strong>
                      </li>
                    </ul>
                  ) : (
                    <p className="myl-rpanel__copy">Collector counts appear when NFT editions are on file.</p>
                  )}
                </div>

                <div className="myl-rpanel dg-gold-ring">
                  <h2 className="myl-rpanel__title">Recent relics</h2>
                  {featuredNfts.length > 0 ? (
                    <ul className="myl-rpanel__relics">
                      {featuredNfts.slice(0, 4).map((nft) => (
                        <li key={`rail-${nft.key}`}>
                          <button type="button" className="myl-rpanel__relic-btn" onClick={() => setNftDrawerId(nft.entitlementId)}>
                            <span className="myl-rpanel__relic-title">{nft.title}</span>
                            <span className="myl-rpanel__relic-meta">{nft.editionName}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="myl-rpanel__copy">Featured covers populate from your vault carousel.</p>
                  )}
                </div>

                <div className="myl-rpanel myl-rpanel--actions dg-gold-ring">
                  <h2 className="myl-rpanel__title">Quick actions</h2>
                  <div className="myl-rpanel__actions">
                    <Link to="/library" className="dg-btn dg-btn--primary dg-btn--compact">
                      Browse library
                    </Link>
                    <Link to="/library#store-nft" className="dg-btn dg-btn--blue-outline dg-btn--compact">
                      Buy editions
                    </Link>
                    <Link to="/dashboard" className="dg-btn dg-btn--gold-outline dg-btn--compact">
                      Gateway
                    </Link>
                  </div>
                </div>
              </aside>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
